import Promise from 'bluebird';
import _ from 'underscore';
import { navigate } from '@bingads-webui-clientcenter/navigation';
import { get, post } from '@bingads-webui-clientcenter/ajax-promise';

import { isPilotEnabled } from './util';

// JavaScript doesn’t have truly private members (before babel7 and #declaration though),
// this dangling underscores naming convention served as a warning to make it obvious that
// these properties are “private” members of GlobalAppsModel object.
/* eslint no-underscore-dangle:0 */
let _deferredResolved;
let _history;
let _i18n;
const _i18nMap = {};

export default class GlobalAppsModel {
  constructor() {
    this.initialized = false;
    this.initializedPromise = new Promise((resolve) => {
      _deferredResolved = resolve;
    });
    _history = undefined;
  }

  setInitialized(value) {
    this.initialized = value;
    this.rejectUninitialized();
    _deferredResolved(this);
  }

  rejectUninitialized() {
    if (!this.initialized || !this.pageContext) {
      throw new Error('GlobalApps need to be initialized');
    }
    return true;
  }

  /**
   * Example:
   * -- import globalApps from '../index';
   * -- globalApps.isPilotEnabled('isUserPreferencesCategoryEnabled')
   * @param {string} pilotFlag - The input string of pilot name
   * @return {boolean} - The pilot enabled status
   */
  isPilotEnabled = (pilotFlag) => {
    this.rejectUninitialized();
    return isPilotEnabled(this.pageContext, pilotFlag);
  };

  /**
   * The options for navigate function
   * @typedef {Object} Options
   * @property {Object} params adding query params to the url
   * @property {Object} state adding state to the history object to pass objects during navigation, used for history only
   */

  /**
   * Function to navigate using the route-configuration's navigate function with private history object set to this instance
   * @param {String} url the url in a string format
   * @param {Options} options options for adding parameters
   * @returns {void}
   */
  navigate = (url, options) => {
    if (this.loggingProxy) {
      this.loggingProxy.flushLogs(() => navigate(url, options, _history));
    } else {
      navigate(url, options, _history);
    }
  };

  /**
   * Defines a configuration object that can be used to customize the behavior of {@link get} and {@link post} methods.
   * @typedef FetchOptions
   * @property {string} [contentType] The content type of the request.
   * Controls how data objects are encoded before being sent to the server.
   * By default it's 'application/x-www-form-urlencoded; charset=UTF-8'.
   * @property {boolean} [crossDomain] Whether to allow cross domain redirects or not.
   * Note that by default cross domain requests are allowed, however requests going to the same server
   * are not allowed to be redirected to another domain; use this setting to override that behavior
   * @property {boolean} [silent] Whether the request should fail silently whithout reporting any errors or not.
   * This can be useful if some data needs to be fetched optimistically in the background
   * and we don't want to disrupt the user's flow if there are any failures
   * (however the failures will have to be dealt with, explicitly, later).
   */

  /**
   * GETs data from the specified URL, automatically handling certan standard responses,
   * like server-reported errors, notifications and redirects, and returns a promise.
   * @param {string} url The URL to get the data from.
   * @param {FetchOptions} [options] Optional settings overrides that can tweak how this method behaves.
   * @returns {Promise} A promise that resolves when the server successfully returned the requested data,
   * or rejects if there were any errors. Network errors are rejected with an instance of {@link AjaxError},
   * while server errors are rejected with an instance of {@link JsonError}.
   */
  get(url, options) {
    return this.initializedPromise.then(() => get(_.extend({
      url,
      redirectHandler: location => this.navigate(location),
      xhrFields: {
        withCredentials: true,
      },
    }, options)));
  }

  /**
   * POSTs data to the specified URL, automatically handling certain standard responses,
   * like server-reported errors, notifications and redirects, and returns a promise.
   * @param {string} url The URL where the data should be POSTed.
   * @param {object} data The data to be POSTed. If this is a string, it will be POSTed as-is;
   * otherwise it will be encoded based on the contentType option, by default 'application/x-www-form-urlencoded; charset=UTF-8'.
   * @param {FetchOptions} [options] Optional settings overrides that can tweak how this method behaves.
   * @returns {Promise} A promise that resolves when the server successfully processed the POSTed data,
   * or rejects if there were any errors. Network errors are rejected with an instance of {@link AjaxError},
   * while server errors are rejected with an instance of {@link JsonError}.
   */
  post(url, data, options) {
    return this.initializedPromise.then(() => post(_.extend({
      url,
      data,
      redirectHandler: location => this.navigate(location),
      xhrFields: {
        withCredentials: true,
      },
    }, options)));
  }

  /**
   * Defines the model for a floating alert.
   * @typedef Alert
   * @property {string} message HTML-encoded content of the alert.
   * @property {'Error'|'Warning'|'Info'|'Confirmation'} level The type of alert to display.
   */

  /**
   * Convenience method for displaying a single floating alert and dismissing any previous alerts.
   * This is usually needed when reporting (transient) errors as a result of a user action.
   * @param {Alert} alert The floating alert to be displayed.
   * @returns {void}
   */
  showAlert(alert) {
    return this.initializedPromise.then(() => {
      this.alertCenter.dismissAll();
      this.alertCenter.show(alert);
    });
  }

  addI18nToMap = (name, i18n) => {
    _i18nMap[name] = i18n;
  };

  getI18nFromMap = (name, throwError = true) => {
    if (throwError && !_.has(_i18nMap, name)) {
      throw new Error(`GlobalApps::I18n with name ${name} not found in the i18nMap!`);
    }

    return _i18nMap[name];
  };

  set i18n(i18n) {
    _i18n = i18n;
  }

  get i18n() {
    if (!_.isObject(_i18n)) {
      throw new Error('GlobalApps::i18n not initialized!');
    }

    return _i18n;
  }

  get basename() {
    return _history && typeof _history.basename === 'string' ? _history.basename : '/cc';
  }

  /**
   * This property is referring to legacy-js/common/instrumentation/log
   * For code where was using:
   * -- import * as logging from 'instrumentation/log'
   * Now we can use:
   * -- import globalApps from '../index';
   * -- globalApps.logging.startScenario(...)
   * @return {Object} - The logging object from legacy-js/common/instrumentation/log
   */
  get logging() {
    this.rejectUninitialized();
    return this.loggingProxy;
  }

  /**
   * This property is referring to legacy-js/common/instrumentation/performance-api
   * For code where was using:
   * -- import * as perfApi from 'instrumentation/performance-api';
   * Now we can use:
   * -- import globalApps from '../index';
   * -- globalApps.perfApi.mark(...)
   * @return {Object} - The perfApi object from legacy-js/common/instrumentation/performance-api
   */
  get perfApi() {
    this.rejectUninitialized();
    return this.perfProxy;
  }

  /**
   * Set the history to globalApps only once, such has it cannot be overriden or modified once set from the App initialization
   * @param {Object} history the react history used for navigation event
   */
  set history(history) {
    if (!_history) {
      _history = history;
    }
  }
}
